import React, { useEffect, useMemo, useState, useCallback } from "react";
import { message, Skeleton, Drawer } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { request } from "../api/client";
import { API_URLS } from "../utils/urls";
import { colors } from "../styles/colors";
import { TRACK_VIEW_MODE, TRACK_VIEW_MODES } from "../constants/trackViewModes";
import { addDays } from "../utils/dateUtils";
import { dateToISO, getWeekNumber, getWeekStart } from "../utils/date";
import { APP_ROUTES } from "../utils/appRoutes";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { fetchTrackFull, saveMilestoneBatchDate } from "../store/dashboardSlice";
import TrackerMilestoneSidebar from "../components/tracker/MilestoneSidebar";
import TrackerWeekGrid from "../components/tracker/WeekGrid";
import TrackerMonthGrid from "../components/tracker/MonthGrid";
import TrackerYearGrid from "../components/tracker/YearGrid";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui";
import { UpgradeModal } from "../components/upgrade";
import { useIsPremium } from "../hooks/useIsPremium";
import { FREE_PLAN_LIMITS } from "../config/upgrade";
import MilestoneNotes from "../components/notes/MilestoneNotes";
import { coerceNotes, cleanNotesForSave, NoteEntry } from "../components/notes/noteTypes";

type DreamItem = {
  id: string;
  title: string;
  color?: string;
};

type GoalItem = {
  id: string;
  dreamId: string;
  title: string;
};

type MilestoneItem = {
  id: string;
  goalId: string;
  title: string;
  description?: string;
  date?: string;
  completed?: boolean;
  notes?: string;
  tasks?: { id: string; text: string; completed: boolean }[];
  hasNote?: boolean;
};

type ScheduledEntry = {
  iso: string;
  dream: DreamItem;
  goal: GoalItem;
  milestone: MilestoneItem;
};

/* ─── Shared button styles ───────────────────────────────────────── */
const NAV_ICON_BUTTON_CLASS =
  "flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border-0 bg-transparent";
/* ─── Main Track (Plan) component ───────────────────────────────── */
export default function Track() {
  const navigate = useNavigate();
  const { dreamId: routeDreamId } = useParams();
  const dispatch = useAppDispatch();
  const {
    dreams: dashboardDreams,
    goals: dashboardGoals,
    milestones: dashboardMilestones,
    loading: dashboardLoading,
    saveMilestoneBatchDateLoading,
  } = useAppSelector((state) => state.dashboard);
  const dreams = dashboardDreams as DreamItem[];
  const goals = dashboardGoals as GoalItem[];
  const milestones = dashboardMilestones as MilestoneItem[];
  const loading = dashboardLoading;
  const isPremium = useIsPremium();
  const { getAccessTokenSilently } = useAuth0();
  const [viewMode, setViewMode] = useState(TRACK_VIEW_MODE.WEEK);
  const [pageTab, setPageTab] = useState<"execute" | "planner">("execute");
  const [executeDreamFilter, setExecuteDreamFilter] = useState<string | null>(null);
  const [showUpgradeGate, setShowUpgradeGate] = useState(false);

  const handleNewDream = () => {
    if (!isPremium && dreams.length >= FREE_PLAN_LIMITS.dreams) {
      setShowUpgradeGate(true);
      return;
    }
    navigate(APP_ROUTES.dreamCreate);
  };

  const handleViewModeChange = (v: string) => {
    if (!isPremium && v !== TRACK_VIEW_MODE.WEEK) {
      setShowUpgradeGate(true);
      return;
    }
    setViewMode(v);
  };
  const [anchorDate, setAnchorDate] = useState<Date>(() => new Date());
  const [selectedDreamId, setSelectedDreamId] = useState<string | null>(null);
  const [draftDates, setDraftDates] = useState<Record<string, string>>({});
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [openMilestoneEntry, setOpenMilestoneEntry] = useState<ScheduledEntry | null>(null);
  const [milestoneNotes, setMilestoneNotes] = useState<NoteEntry[]>([]);
  const [milestoneTasks, setMilestoneTasks] = useState<{ id: string; text: string; completed: boolean }[]>([]);
  const [newTaskText, setNewTaskText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [noteLoading, setNoteLoading] = useState(false);

  const goalsByDream = useMemo(() => {
    const goalsMap: Record<string, GoalItem[]> = {};
    for (const goal of goals || []) {
      const dreamId = goal.dreamId;
      if (!dreamId) continue;
      goalsMap[dreamId] = goalsMap[dreamId] || [];
      goalsMap[dreamId].push(goal);
    }
    return goalsMap;
  }, [goals]);

  const milestonesByGoal = useMemo(() => {
    const milestonesMap: Record<string, MilestoneItem[]> = {};
    for (const milestone of milestones || []) {
      const goalId = milestone.goalId;
      if (!goalId) continue;
      milestonesMap[goalId] = milestonesMap[goalId] || [];
      milestonesMap[goalId].push(milestone);
    }
    return milestonesMap;
  }, [milestones]);

  useEffect(() => {
    dispatch(fetchTrackFull());
  }, [dispatch]);

  useEffect(() => {
    if (!dreams.length) return;

    const routeDreamExists = routeDreamId
      ? dreams.some((dream) => dream.id === routeDreamId)
      : false;

    if (routeDreamExists && routeDreamId !== selectedDreamId) {
      setSelectedDreamId(routeDreamId);
      return;
    }

    if (routeDreamId && !routeDreamExists) {
      const fallbackDreamId = dreams[0].id;
      setSelectedDreamId(fallbackDreamId);
      navigate(APP_ROUTES.planDream(fallbackDreamId), { replace: true });
      return;
    }

    if (!routeDreamId) {
      const initialDreamId = selectedDreamId || dreams[0].id;
      if (initialDreamId !== selectedDreamId) {
        setSelectedDreamId(initialDreamId);
      }
      navigate(APP_ROUTES.planDream(initialDreamId), { replace: true });
    }
  }, [dreams, routeDreamId, selectedDreamId, navigate]);

  /* ── Selected dream ── */
  const selectedDream = useMemo(() => {
    if (!dreams.length) return null;
    return dreams.find((d) => d.id === selectedDreamId) || dreams[0];
  }, [dreams, selectedDreamId]);

  /* ── Period label ── */
  const periodLabel = useMemo(() => {
    if (viewMode === TRACK_VIEW_MODE.WEEK) {
      const ws = getWeekStart(anchorDate);
      const we = addDays(ws, 6);
      const wn = getWeekNumber(ws);
      return {
        primary: ws.toLocaleDateString("en-US", { month: "long", year: "numeric" }),
        secondary: `Week #${wn} · ${ws.getDate()} – ${we.getDate()}`,
      };
    }
    if (viewMode === TRACK_VIEW_MODE.MONTH) return {
      primary: anchorDate.toLocaleDateString("en-US", { month: "long", year: "numeric" }),
      secondary: null,
    };
    return { primary: String(anchorDate.getFullYear()), secondary: null };
  }, [viewMode, anchorDate]);

  /* ── Period navigation ── */
  const navPrev = useCallback(() => setAnchorDate((d) =>
    viewMode === TRACK_VIEW_MODE.WEEK ? addDays(d, -7) :
    viewMode === TRACK_VIEW_MODE.MONTH ? new Date(d.getFullYear(), d.getMonth() - 1, 1) :
    new Date(d.getFullYear() - 1, d.getMonth(), 1)
  ), [viewMode]);
  const navNext = useCallback(() => setAnchorDate((d) =>
    viewMode === TRACK_VIEW_MODE.WEEK ? addDays(d, 7) :
    viewMode === TRACK_VIEW_MODE.MONTH ? new Date(d.getFullYear(), d.getMonth() + 1, 1) :
    new Date(d.getFullYear() + 1, d.getMonth(), 1)
  ), [viewMode]);
  const navToday = useCallback(() => setAnchorDate(new Date()), []);

  const milestoneLocationById = useMemo(() => {
    const locationMap: Record<string, { dreamId: string; goalId: string; savedDate: string }> = {};
    for (const dream of dreams) {
      const goalsForDream = goalsByDream[dream.id] || [];
      for (const goal of goalsForDream) {
        const milestonesForGoal = milestonesByGoal[goal.id] || [];
        for (const milestone of milestonesForGoal) {
          locationMap[milestone.id] = {
            dreamId: dream.id,
            goalId: goal.id,
            savedDate: milestone.date || "",
          };
        }
      }
    }
    return locationMap;
  }, [dreams, goalsByDream, milestonesByGoal]);

  /* ── Save changes to API ── */
  const saveChanges = useCallback(async (dates = draftDates) => {
    const updates = [];
    for (const [milestoneId, draftDate] of Object.entries(dates)) {
      const milestoneLocation = milestoneLocationById[milestoneId];
      if (!milestoneLocation) continue;
      if (milestoneLocation.savedDate === draftDate) continue;

      updates.push({
        dreamId: milestoneLocation.dreamId,
        goalId: milestoneLocation.goalId,
        milestoneId,
        date: draftDate,
      });
    }

    if (!updates.length) return;
    try {
      await dispatch(saveMilestoneBatchDate({ updates })).unwrap();
      setDraftDates({});
      message.success("Plan saved");
    } catch (e) {
      message.error(e.message || "Failed to save plan");
    }
  }, [
    draftDates,
    milestoneLocationById,
    dispatch,
  ]);

  /* ── Drag & drop ── */
  const handleDragStart = useCallback((event: React.DragEvent, milestoneId: string) => {
    try { event.dataTransfer.setData("application/json", JSON.stringify({ milestoneId })); } catch (_) {}
    setDragId(milestoneId);
  }, []);
  const handleDragEnd = useCallback(() => { setDragId(null); setDropTarget(null); }, []);
  const handleDrop = useCallback((event: React.DragEvent, iso: string) => {
    event.preventDefault();
    let milestoneId: string | null = null;
    try {
      const raw = event.dataTransfer.getData("application/json");
      if (raw) milestoneId = JSON.parse(raw).milestoneId;
    } catch (_) {}
    if (!milestoneId) return;

    setDraftDates((prev) => {
      const nextDates = { ...prev, [milestoneId]: iso };
      saveChanges(nextDates);
      return nextDates;
    });
    setDragId(null);
    setDropTarget(null);
  }, [saveChanges]);
  const handleRemove = useCallback((milestoneId: string) => {
    setDraftDates((prev) => {
      const nextDates = { ...prev, [milestoneId]: "" };
      saveChanges(nextDates);
      return nextDates;
    });
  }, [saveChanges]);

  const handleMarkDone = useCallback(async (entry: ScheduledEntry) => {
    try {
      const token = await getAccessTokenSilently({ cacheMode: "off" });
      await request(API_URLS.milestone(entry.dream.id, entry.goal.id, entry.milestone.id), {
        method: "PUT",
        auth: true,
        getAccessToken: () => Promise.resolve(token),
        body: { completed: !entry.milestone.completed },
      });
      dispatch(fetchTrackFull());
      message.success(!entry.milestone.completed ? "Marked as done!" : "Marked as not done");
    } catch (e: any) {
      message.error(e?.message || "Failed to update");
    }
  }, [getAccessTokenSilently, dispatch]);

  const handleOpenMilestone = useCallback(async (entry: ScheduledEntry) => {
    setOpenMilestoneEntry(entry);
    setMilestoneNotes(coerceNotes(entry.milestone.notes));
    setMilestoneTasks(Array.isArray(entry.milestone.tasks) ? entry.milestone.tasks : []);
    setNewTaskText("");
    setNoteLoading(true);
    try {
      const token = await getAccessTokenSilently({ cacheMode: "off" });
      const data = await request(API_URLS.milestone(entry.dream.id, entry.goal.id, entry.milestone.id), {
        auth: true,
        getAccessToken: () => Promise.resolve(token),
      });
      setMilestoneNotes(coerceNotes(data?.notes));
      setMilestoneTasks(Array.isArray(data?.tasks) ? data.tasks : []);
    } catch {
      // fall back to data already in store
    } finally {
      setNoteLoading(false);
    }
  }, [getAccessTokenSilently]);

  const handleCloseMilestone = useCallback(() => {
    setOpenMilestoneEntry(null);
    setMilestoneNotes([]);
    setMilestoneTasks([]);
    setNewTaskText("");
  }, []);

  const persistMilestone = async (
    notes: NoteEntry[],
    tasks: { id: string; text: string; completed: boolean }[],
  ) => {
    if (!openMilestoneEntry) return;
    setSavingNote(true);
    try {
      const token = await getAccessTokenSilently({ cacheMode: "off" });
      await request(API_URLS.milestone(openMilestoneEntry.dream.id, openMilestoneEntry.goal.id, openMilestoneEntry.milestone.id), {
        method: "PUT",
        auth: true,
        getAccessToken: () => Promise.resolve(token),
        body: { notes: cleanNotesForSave(notes), tasks },
      });
    } catch (e: any) {
      message.error(e?.message || "Failed to save");
    } finally {
      setSavingNote(false);
    }
  };

  const handleRevealNote = useCallback(async (noteId: string): Promise<string | null> => {
    if (!openMilestoneEntry) return null;
    try {
      const token = await getAccessTokenSilently({ cacheMode: "off" });
      const data = await request(
        API_URLS.milestoneNoteReveal(openMilestoneEntry.dream.id, openMilestoneEntry.goal.id, openMilestoneEntry.milestone.id, noteId),
        { method: "POST", auth: true, getAccessToken: () => Promise.resolve(token) }
      );
      return data?.content ?? "";
    } catch (e: any) {
      message.error(e?.message || "Failed to unlock");
      return null;
    }
  }, [openMilestoneEntry, getAccessTokenSilently]);

  const handleToggleTask = (taskId: string) => {
    const next = milestoneTasks.map((t) => t.id === taskId ? { ...t, completed: !t.completed } : t);
    setMilestoneTasks(next);
    persistMilestone(milestoneNotes, next);
  };

  const handleAddTask = () => {
    const text = newTaskText.trim();
    if (!text) return;
    const next = [...milestoneTasks, { id: `${Date.now()}`, text, completed: false }];
    setMilestoneTasks(next);
    setNewTaskText("");
    persistMilestone(milestoneNotes, next);
  };

  const handleRemoveTask = (taskId: string) => {
    const next = milestoneTasks.filter((t) => t.id !== taskId);
    setMilestoneTasks(next);
    persistMilestone(milestoneNotes, next);
  };

  const handleSelectDream = useCallback((dreamId: string) => {
    setSelectedDreamId(dreamId);
    navigate(APP_ROUTES.planDream(dreamId));
  }, [navigate]);

  /* ── Effective date: draft overrides saved ── */
  const effectiveDate = useCallback((milestone: MilestoneItem) => {
    const draftDate = draftDates[milestone.id];
    return draftDate !== undefined ? draftDate : milestone.date || "";
  }, [draftDates]);

  /* ── Week grid data ── */
  const weekDays = useMemo(() => {
    const ws = getWeekStart(anchorDate);
    return Array.from({ length: 7 }, (_, i) => addDays(ws, i));
  }, [anchorDate]);

  const scheduledEntries = useMemo(() => {
    const entries: ScheduledEntry[] = [];
    for (const dream of dreams) {
      const goalsForDream = goalsByDream[dream.id] || [];
      for (const goal of goalsForDream) {
        const milestonesForGoal = milestonesByGoal[goal.id] || [];
        for (const milestone of milestonesForGoal) {
          const iso = effectiveDate(milestone);
          if (iso) {
            entries.push({ iso, dream, goal, milestone });
          }
        }
      }
    }
    return entries;
  }, [dreams, goalsByDream, milestonesByGoal, effectiveDate]);

  const weekGridByDay = useMemo(() => {
    const weekScheduleMap: Record<string, ScheduledEntry[]> = {};
    for (const day of weekDays) {
      weekScheduleMap[dateToISO(day)] = [];
    }
    for (const entry of scheduledEntries) {
      if (!weekScheduleMap[entry.iso]) continue;
      weekScheduleMap[entry.iso].push(entry);
    }
    return weekScheduleMap;
  }, [weekDays, scheduledEntries]);

  /* ── Month and year view scheduled data ── */
  const scheduledByDate = useMemo(() => {
    const scheduleByDateMap: Record<string, ScheduledEntry[]> = {};
    for (const entry of scheduledEntries) {
      if (!scheduleByDateMap[entry.iso]) {
        scheduleByDateMap[entry.iso] = [];
      }
      scheduleByDateMap[entry.iso].push(entry);
    }
    return scheduleByDateMap;
  }, [scheduledEntries]);

  /* ── Execute tab filtered data ── */
  const executeScheduledEntries = useMemo(() =>
    executeDreamFilter ? scheduledEntries.filter(e => e.dream.id === executeDreamFilter) : scheduledEntries,
    [scheduledEntries, executeDreamFilter]
  );

  const executeWeekGridByDay = useMemo(() => {
    const map: Record<string, ScheduledEntry[]> = {};
    for (const day of weekDays) map[dateToISO(day)] = [];
    for (const entry of executeScheduledEntries) {
      if (map[entry.iso]) map[entry.iso].push(entry);
    }
    return map;
  }, [weekDays, executeScheduledEntries]);

  const executeScheduledByDate = useMemo(() => {
    const map: Record<string, ScheduledEntry[]> = {};
    for (const entry of executeScheduledEntries) {
      if (!map[entry.iso]) map[entry.iso] = [];
      map[entry.iso].push(entry);
    }
    return map;
  }, [executeScheduledEntries]);

  /* ── Render ── */
  return (
    <div className="flex flex-col gap-5 pb-20">

      {/* ── Toolbar ── */}
      <PageHeader
        title="Dreams Planner"
        subtitle="Schedule your dream milestones across the week, month, or year."
        right={
          <div className="flex w-full items-center gap-2 sm:w-auto">
          <div
            className="flex flex-1 items-center rounded-xl bg-[#fafafa] p-1 sm:flex-none"
            style={{ border: `1px solid ${colors.borderLight}` }}
          >
            <button type="button" onClick={navPrev} className={NAV_ICON_BUTTON_CLASS} style={{ color: colors.textTertiary }} aria-label="Previous">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M9 2L4 7L9 12" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <div className="flex-1 px-2 text-center md:min-w-40 md:px-3">
              <p className="m-0 text-[13px] font-bold" style={{ color: colors.textPrimary }}>
                {periodLabel.primary}
              </p>
              {periodLabel.secondary && (
                <p className="m-0 text-[11px]" style={{ color: colors.textTertiary }}>
                  {periodLabel.secondary}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={navToday}
              className="cursor-pointer rounded-lg border-0 bg-transparent px-3 py-1.5 text-[13px] font-semibold"
              style={{ color: colors.brandPrimary }}
            >
              Today
            </button>
            <button type="button" onClick={navNext} className={NAV_ICON_BUTTON_CLASS} style={{ color: colors.textTertiary }} aria-label="Next">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M5 2L10 7L5 12" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
          <Button
            variant="primary"
            size="md"
            onClick={handleNewDream}
            aria-label="Create a new dream"
            className="hidden sm:flex"
          >
            + New dream
          </Button>
          </div>
        }
      />

      {/* ── Page tabs ── */}
      <div style={{ display: "flex", alignItems: "flex-end", borderBottom: "2px solid #e7ebf0" }}>
        <div style={{ display: "flex", gap: 0 }}>
          {(["execute", "planner"] as const).map((tab) => {
            const isActive = pageTab === tab;
            return (
              <button
                key={tab}
                type="button"
                onClick={() => setPageTab(tab)}
                style={{
                  border: "none",
                  borderBottom: isActive ? `2px solid ${colors.brandPrimary}` : "2px solid transparent",
                  background: "transparent",
                  padding: "8px 20px",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                  color: isActive ? colors.brandPrimary : "rgba(0,0,0,0.4)",
                  marginBottom: -2,
                  transition: "color 0.15s, border-color 0.15s",
                }}
              >
                {tab === "execute" ? "Execute" : "Planner"}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Dream selector ── */}
      {pageTab === "planner" && !loading && dreams.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {dreams.map((dream) => {
            const isSelected = selectedDream?.id === dream.id;
            const chipColor = dream.color || colors.brandPrimary;
            return (
              <button
                key={dream.id}
                type="button"
                onClick={() => handleSelectDream(dream.id)}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "5px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                  cursor: "pointer", transition: "all 0.15s",
                  background: isSelected ? chipColor : "#ffffff",
                  color: isSelected ? "#ffffff" : colors.textSecondary,
                  border: `1px solid ${isSelected ? chipColor : colors.borderLight}`,
                }}
              >
                <span style={{ width: 7, height: 7, borderRadius: "50%", flexShrink: 0, background: isSelected ? "rgba(255,255,255,0.75)" : chipColor }} />
                {dream.title}
              </button>
            );
          })}
        </div>
      )}

      {/* ── Loading skeleton ── */}
      {loading && <Skeleton active paragraph={{ rows: 8 }} />}

      {/* ── Two-panel body (Planner tab) ── */}
      {pageTab === "planner" && !loading && dreams.length > 0 && (
        <div className="flex min-h-[540px] flex-col items-start gap-4 lg:flex-row">
          <div className="flex w-full flex-col gap-2 lg:w-[272px] lg:flex-shrink-0">
            {selectedDream && (() => {
              const goalsForDream = goalsByDream[selectedDream.id] || [];
              const milestoneCount = goalsForDream.reduce((t, g) => t + (milestonesByGoal[g.id] || []).length, 0);
              return (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "0 4px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                    <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: selectedDream.color || colors.brandPrimary, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: colors.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {selectedDream.title}
                    </span>
                  </div>
                  <span style={{ fontSize: 11, color: colors.textTertiary, flexShrink: 0 }}>
                    {goalsForDream.length} goals · {milestoneCount} milestones
                  </span>
                </div>
              );
            })()}
            <TrackerMilestoneSidebar
              dream={selectedDream}
              goalsByDream={goalsByDream}
              milestonesByGoal={milestonesByGoal}
              draftDates={draftDates}
              dragId={dragId}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onRemove={handleRemove}
              onOpenMilestone={handleOpenMilestone}
            />
          </div>
          <div
            className="flex w-full flex-1 flex-col self-start overflow-hidden rounded-2xl bg-white shadow-sm lg:sticky lg:top-4"
            style={{ border: `1px solid ${colors.borderLight}` }}
          >
            {/* Unified calendar header */}
            <div
              className="flex items-center justify-between bg-[rgba(0,0,0,0.015)] px-5 py-3.5"
              style={{ borderBottom: `1px solid ${colors.borderLight}` }}
            >
              <div>
                <h3 className="m-0 text-[13px] font-semibold" style={{ color: colors.textPrimary }}>
                  {viewMode === TRACK_VIEW_MODE.WEEK && "My Week Plan"}
                  {viewMode === TRACK_VIEW_MODE.MONTH && "My Month Plan"}
                  {viewMode === TRACK_VIEW_MODE.YEAR && "My Year Plan"}
                </h3>
                <p className="m-0 mt-0.5 text-[11px]" style={{ color: colors.textTertiary }}>
                  {viewMode === TRACK_VIEW_MODE.YEAR
                    ? "Drop milestones onto a month (lands on 1st)"
                    : "Drag milestones onto a day"}
                </p>
              </div>
              <div className="inline-flex rounded-xl bg-[#f5f5f5] p-0.5 md:p-1">
                {TRACK_VIEW_MODES.map((v) => {
                  const isLocked = !isPremium && v !== TRACK_VIEW_MODE.WEEK;
                  const isActive = viewMode === v;
                  return (
                    <button
                      key={v}
                      type="button"
                      onClick={() => handleViewModeChange(v)}
                      className="relative flex cursor-pointer items-center gap-1 rounded-lg border-0 px-2.5 py-1.5 text-[12px] font-semibold transition-all md:gap-1.5 md:px-4 md:py-2 md:text-[13px]"
                      style={{
                        background: isActive ? "#ffffff" : "transparent",
                        color: isActive ? colors.brandPrimary : isLocked ? colors.textTertiary : colors.textTertiary,
                        boxShadow: isActive ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
                        opacity: isLocked ? 0.75 : 1,
                      }}
                      title={isLocked ? "Upgrade to Premium to unlock this view" : undefined}
                    >
                      {v.charAt(0).toUpperCase() + v.slice(1)}
                      {isLocked && (
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ flexShrink: 0 }}>
                          <rect x="1.5" y="4.5" width="7" height="5" rx="1.2" stroke={colors.textTertiary} strokeWidth="1.2"/>
                          <path d="M3 4.5V3.5a2 2 0 1 1 4 0v1" stroke={colors.textTertiary} strokeWidth="1.2" strokeLinecap="round"/>
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {viewMode === TRACK_VIEW_MODE.WEEK && (
              <TrackerWeekGrid
                weekDays={weekDays}
                gridByDay={weekGridByDay}
                dragId={dragId}
                dropTarget={dropTarget}
                setDropTarget={setDropTarget}
                onDrop={handleDrop}
                onRemove={handleRemove}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onOpenMilestone={handleOpenMilestone}
              />
            )}
            {viewMode === TRACK_VIEW_MODE.MONTH && (
              <TrackerMonthGrid
                anchorDate={anchorDate}
                scheduledByDate={scheduledByDate}
                dragId={dragId}
                dropTarget={dropTarget}
                setDropTarget={setDropTarget}
                onDrop={handleDrop}
                onRemove={handleRemove}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onOpenMilestone={handleOpenMilestone}
              />
            )}
            {viewMode === TRACK_VIEW_MODE.YEAR && (
              <TrackerYearGrid
                year={anchorDate.getFullYear()}
                allScheduled={scheduledEntries}
                dragId={dragId}
                dropTarget={dropTarget}
                setDropTarget={setDropTarget}
                onDrop={handleDrop}
                onRemove={handleRemove}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onOpenMilestone={handleOpenMilestone}
              />
            )}
          </div>
        </div>
      )}

      {/* ── Execute tab ── */}
      {pageTab === "execute" && !loading && dreams.length > 0 && (
        <>
        {/* Dream filter chips */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setExecuteDreamFilter(null)}
            style={{
              padding: "5px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600,
              cursor: "pointer", transition: "all 0.15s",
              background: executeDreamFilter === null ? colors.brandPrimary : "#ffffff",
              color: executeDreamFilter === null ? "#ffffff" : colors.textTertiary,
              border: executeDreamFilter === null ? `1px solid ${colors.brandPrimary}` : `1px solid ${colors.borderLight}`,
            }}
          >All dreams</button>
          {dreams.map((dream) => {
            const isActive = executeDreamFilter === dream.id;
            const chipColor = dream.color || colors.brandPrimary;
            return (
              <button
                key={dream.id}
                type="button"
                onClick={() => setExecuteDreamFilter(isActive ? null : dream.id)}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "5px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                  cursor: "pointer", transition: "all 0.15s",
                  background: isActive ? chipColor : "#ffffff",
                  color: isActive ? "#ffffff" : colors.textSecondary,
                  border: `1px solid ${isActive ? chipColor : colors.borderLight}`,
                }}
              >
                <span style={{ width: 7, height: 7, borderRadius: "50%", flexShrink: 0, background: isActive ? "rgba(255,255,255,0.75)" : chipColor }} />
                {dream.title}
              </button>
            );
          })}
        </div>
        <div
          className="flex w-full flex-col overflow-hidden rounded-2xl bg-white shadow-sm"
          style={{ border: `1px solid ${colors.borderLight}` }}
        >
          <div
            className="flex items-center justify-between bg-[rgba(0,0,0,0.015)] px-5 py-3.5"
            style={{ borderBottom: `1px solid ${colors.borderLight}` }}
          >
            <div>
              <h3 className="m-0 text-[13px] font-semibold" style={{ color: colors.textPrimary }}>
                {viewMode === TRACK_VIEW_MODE.WEEK && "This Week's Milestones"}
                {viewMode === TRACK_VIEW_MODE.MONTH && "This Month's Milestones"}
                {viewMode === TRACK_VIEW_MODE.YEAR && "This Year's Milestones"}
              </h3>
              <p className="m-0 mt-0.5 text-[11px]" style={{ color: colors.textTertiary }}>
                Tap ✓ on any milestone to mark it as done
              </p>
            </div>
            <div className="inline-flex rounded-xl bg-[#f5f5f5] p-0.5 md:p-1">
              {TRACK_VIEW_MODES.map((v) => {
                const isLocked = !isPremium && v !== TRACK_VIEW_MODE.WEEK;
                const isActive = viewMode === v;
                return (
                  <button
                    key={v}
                    type="button"
                    onClick={() => handleViewModeChange(v)}
                    className="relative flex cursor-pointer items-center gap-1 rounded-lg border-0 px-2.5 py-1.5 text-[12px] font-semibold transition-all md:gap-1.5 md:px-4 md:py-2 md:text-[13px]"
                    style={{
                      background: isActive ? "#ffffff" : "transparent",
                      color: isActive ? colors.brandPrimary : colors.textTertiary,
                      boxShadow: isActive ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
                      opacity: isLocked ? 0.75 : 1,
                    }}
                    title={isLocked ? "Upgrade to Premium to unlock this view" : undefined}
                  >
                    {v.charAt(0).toUpperCase() + v.slice(1)}
                    {isLocked && (
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ flexShrink: 0 }}>
                        <rect x="1.5" y="4.5" width="7" height="5" rx="1.2" stroke={colors.textTertiary} strokeWidth="1.2"/>
                        <path d="M3 4.5V3.5a2 2 0 1 1 4 0v1" stroke={colors.textTertiary} strokeWidth="1.2" strokeLinecap="round"/>
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          {viewMode === TRACK_VIEW_MODE.WEEK && (
            <TrackerWeekGrid
              weekDays={weekDays}
              gridByDay={executeWeekGridByDay}
              dragId={null}
              dropTarget={null}
              setDropTarget={() => {}}
              onDrop={() => {}}
              onRemove={() => {}}
              onDragStart={() => {}}
              onDragEnd={() => {}}
              onOpenMilestone={handleOpenMilestone}
              executeMode={true}
              onMarkDone={handleMarkDone}
            />
          )}
          {viewMode === TRACK_VIEW_MODE.MONTH && (
            <TrackerMonthGrid
              anchorDate={anchorDate}
              scheduledByDate={executeScheduledByDate}
              dragId={null}
              dropTarget={null}
              setDropTarget={() => {}}
              onDrop={() => {}}
              onRemove={() => {}}
              onDragStart={() => {}}
              onDragEnd={() => {}}
              onOpenMilestone={handleOpenMilestone}
              executeMode={true}
              onMarkDone={handleMarkDone}
            />
          )}
          {viewMode === TRACK_VIEW_MODE.YEAR && (
            <TrackerYearGrid
              year={anchorDate.getFullYear()}
              allScheduled={executeScheduledEntries}
              dragId={null}
              dropTarget={null}
              setDropTarget={() => {}}
              onDrop={() => {}}
              onRemove={() => {}}
              onDragStart={() => {}}
              onDragEnd={() => {}}
              onOpenMilestone={handleOpenMilestone}
              executeMode={true}
              onMarkDone={handleMarkDone}
            />
          )}
        </div>
        </>
      )}

      {/* ── Empty state ── */}
      {!loading && dreams.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-2xl bg-white py-20 text-center shadow-sm" style={{ border: `1px solid ${colors.borderLight}` }}>
          <svg width="52" height="52" viewBox="0 0 52 52" fill="none" className="mb-4 opacity-25">
            <path d="M26 6 L30.5 18.5 L44 18.5 L33.5 26.5 L37.5 39 L26 31.5 L14.5 39 L18.5 26.5 L8 18.5 L21.5 18.5 Z" stroke="#64748B" strokeWidth="2.5" strokeLinejoin="round" fill="none"/>
            <circle cx="26" cy="6" r="2" fill="#64748B" opacity="0.5"/>
            <circle cx="44" cy="18.5" r="1.5" fill="#64748B" opacity="0.4"/>
            <circle cx="8" cy="18.5" r="1.5" fill="#64748B" opacity="0.4"/>
          </svg>
          <p className="m-0 text-[15px] font-semibold" style={{ color: colors.textPrimary }}>No dreams yet</p>
          <p className="m-0 mt-1 text-[13px]" style={{ color: colors.textTertiary }}>Create your first dream to start planning milestones</p>
          <Button
            variant="primary"
            size="md"
            onClick={() => navigate(APP_ROUTES.dreamCreate)}
            className="mt-5"
          >
            + New dream
          </Button>
        </div>
      )}

      <Drawer
        open={!!openMilestoneEntry}
        onClose={handleCloseMilestone}
        placement="right"
        width="min(640px, 92vw)"
        title={
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: colors.textPrimary }}>
              Milestone details
            </div>
            {openMilestoneEntry && (
              <div style={{ fontSize: 12, color: colors.textTertiary, marginTop: 4 }}>
                {openMilestoneEntry.dream.title} · {openMilestoneEntry.goal.title}
              </div>
            )}
          </div>
        }
      >
        {openMilestoneEntry ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

            {/* ── Info ── */}
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: colors.textTertiary, marginBottom: 4 }}>Milestone</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: colors.textPrimary, wordBreak: "break-word" }}>
                  {openMilestoneEntry.milestone.title}
                </div>
              </div>
              {openMilestoneEntry.milestone.description && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: colors.textTertiary, marginBottom: 4 }}>Description</div>
                  <div style={{ fontSize: 13, color: colors.textSecondary, whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                    {openMilestoneEntry.milestone.description}
                  </div>
                </div>
              )}
              <div style={{ display: "flex", gap: 24 }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: colors.textTertiary, marginBottom: 2 }}>Date</div>
                  <div style={{ fontSize: 13, color: colors.textPrimary }}>
                    {openMilestoneEntry.iso
                      ? new Date(`${openMilestoneEntry.iso}T00:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })
                      : "—"}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: colors.textTertiary, marginBottom: 2 }}>Status</div>
                  <div style={{ fontSize: 13, color: openMilestoneEntry.milestone.completed ? "#16a34a" : colors.textPrimary }}>
                    {openMilestoneEntry.milestone.completed ? "✓ Completed" : "Not completed"}
                  </div>
                </div>
              </div>
            </div>

            <div style={{ borderTop: "1px solid #f1f5f9" }} />

            {(() => {
              const isReadOnly = !!openMilestoneEntry.milestone.completed;
              return (
                <>
                  {isReadOnly && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "8px 12px" }}>
                      <span style={{ fontSize: 13, color: "#16a34a", fontWeight: 600 }}>✓ Completed — notes & tasks are read-only</span>
                    </div>
                  )}

                  {/* ── Notes ── */}
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: colors.textPrimary, marginBottom: 8, letterSpacing: 0.3 }}>Notes</div>
                    {noteLoading ? (
                      <div style={{ height: 80, background: "#f8fafc", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <span style={{ fontSize: 12, color: colors.textTertiary }}>Loading…</span>
                      </div>
                    ) : (
                      <MilestoneNotes
                        notes={milestoneNotes}
                        onChange={(next) => { setMilestoneNotes(next); persistMilestone(next, milestoneTasks); }}
                        isPremium={isPremium}
                        readOnly={isReadOnly}
                        onReveal={handleRevealNote}
                        onUpgrade={() => setShowUpgradeGate(true)}
                      />
                    )}
                  </div>

                  {/* ── Todo list ── */}
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: colors.textPrimary, marginBottom: 8, letterSpacing: 0.3 }}>Tasks</div>
                    {noteLoading ? (
                      <div style={{ height: 40, background: "#f8fafc", borderRadius: 8 }} />
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {milestoneTasks.length === 0 && (
                          <div style={{ fontSize: 12, color: colors.textTertiary, padding: "6px 0" }}>
                            {isReadOnly ? "No tasks." : "No tasks yet. Add one below."}
                          </div>
                        )}
                        {milestoneTasks.map((task) => (
                          <div key={task.id} style={{ display: "flex", alignItems: "center", gap: 8, background: task.completed ? "#f0fdf4" : "#f8fafc", borderRadius: 8, padding: "8px 10px", border: task.completed ? "1px solid #bbf7d0" : "1px solid rgba(0,0,0,0.07)" }}>
                            <input
                              type="checkbox"
                              checked={task.completed}
                              onChange={isReadOnly ? undefined : () => handleToggleTask(task.id)}
                              disabled={isReadOnly}
                              style={{ width: 15, height: 15, cursor: isReadOnly ? "default" : "pointer", flexShrink: 0, accentColor: colors.brandPrimary }}
                            />
                            <span style={{ flex: 1, fontSize: 13, color: task.completed ? "#16a34a" : colors.textPrimary, textDecoration: task.completed ? "line-through" : "none", lineHeight: 1.4 }}>
                              {task.text}
                            </span>
                            {!isReadOnly && (
                              <button
                                type="button"
                                onClick={() => handleRemoveTask(task.id)}
                                style={{ appearance: "none", border: "none", background: "transparent", cursor: "pointer", color: "rgba(0,0,0,0.3)", fontSize: 16, lineHeight: 1, padding: 0, flexShrink: 0 }}
                                title="Remove task"
                              >×</button>
                            )}
                          </div>
                        ))}
                        {/* Add task input — hidden when read-only */}
                        {!isReadOnly && (
                          <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                            <input
                              type="text"
                              value={newTaskText}
                              onChange={(e) => setNewTaskText(e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter") handleAddTask(); }}
                              placeholder="New task…"
                              style={{
                                flex: 1, border: "1px solid rgba(0,0,0,0.12)", borderRadius: 8,
                                padding: "7px 10px", fontSize: 13, outline: "none", fontFamily: "inherit",
                                background: "#fafafa", color: colors.textPrimary,
                              }}
                            />
                            <button
                              type="button"
                              onClick={handleAddTask}
                              disabled={!newTaskText.trim()}
                              style={{
                                padding: "7px 14px", borderRadius: 8, border: "none",
                                background: newTaskText.trim() ? colors.brandPrimary : "#e2e8f0",
                                color: newTaskText.trim() ? "#fff" : "#94a3b8",
                                fontSize: 13, fontWeight: 600, cursor: newTaskText.trim() ? "pointer" : "default",
                                transition: "all 0.15s",
                              }}
                            >Add</button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* ── Auto-save status ── */}
                  {!isReadOnly && (
                    <div style={{ textAlign: "center", fontSize: 12, color: colors.textTertiary, minHeight: 16 }}>
                      {savingNote ? "Saving…" : "All changes saved automatically"}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        ) : null}
      </Drawer>
      <UpgradeModal
        open={showUpgradeGate}
        trigger="planner_view"
        onClose={() => setShowUpgradeGate(false)}
      />
    </div>
  );
}
